import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { api } from '../utils/api';

function ProductDetail({ onAddToCart }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [feedbacks, setFeedbacks] = useState([]);
  const [showImageModal, setShowImageModal] = useState(false);
  const [selectedImage, setSelectedImage] = useState('');
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  useEffect(() => {
    window.scrollTo(0, 0);
    loadProduct();
    loadFeedbacks();
  }, [id]);

  const loadFeedbacks = async () => {
    // Skip API calls during pre-rendering
    if (navigator.userAgent === 'ReactSnap') return;
    
    // Get product ID first
    const productResponse = await fetch(`${api.baseURL}/products.php?slug=${id}`);
    const productData = await productResponse.json();
    
    if (productData && productData.id) {
      const response = await fetch(`${api.baseURL}/feedback.php?product_id=${productData.id}`);
      const result = await response.json();
      if (result.success) {
        setFeedbacks(result.data);
      }
    }
  };

  const loadProduct = async () => {
    // Skip API calls during pre-rendering
    if (navigator.userAgent === 'ReactSnap') return;
    
    const response = await fetch(`${api.baseURL}/products.php?slug=${id}`);
    const product = await response.json();
    
    // Check if redirect is needed
    if (product && product.redirect && product.slug) {
      navigate(`/product/${product.slug}`, { replace: true });
      return;
    }
    
    // If slug fetch fails, try with ID
    if (!product || product === false) {
      const idResponse = await fetch(`${api.baseURL}/products.php?id=${id}`);
      const productById = await idResponse.json();
      
      // Check if redirect is needed for ID-based fetch
      if (productById && productById.redirect && productById.slug) {
        navigate(`/product/${productById.slug}`, { replace: true });
        return;
      }
      
      if (productById && productById !== false) {
        console.log('Product loaded by ID:', productById);
        setProduct(productById);
        return;
      }

      setProduct(null);
      return;
    }
    
    console.log('Product loaded:', product);
    console.log('Images type:', typeof product.images);
    console.log('Images value:', product.images);
    
    // Parse images if it's a JSON string (handle multiple encoding levels)
    if (product.images) {
      if (typeof product.images === 'string') {
        let parsed = product.images;
        // Handle multiple levels of encoding
        while (typeof parsed === 'string' && (parsed.startsWith('[') || parsed.startsWith('"'))) {
          try {
            parsed = JSON.parse(parsed);
            console.log('Parsed images:', parsed);
          } catch (e) {
            console.error('Failed to parse images:', e);
            break;
          }
        }
        product.images = Array.isArray(parsed) ? parsed : [];
      } else if (!Array.isArray(product.images)) {
        product.images = [];
      }
    } else {
      product.images = [];
    }
    
    console.log('Final images array:', product.images);
    setProduct(product);
  };

  const getCustomFields = () => {
    const saved = localStorage.getItem('customFields');
    return saved ? JSON.parse(saved) : [];
  };

  if (!product) {
    return <div className="container" style={{padding: '40px', textAlign: 'center'}}>Loading...</div>;
  }

  const refreshProduct = async () => {
    await loadProduct();
  };

  return (
    <div className="container" style={{padding: '40px 20px'}}>
      {product && (
        <>
          <Helmet>
            <title>{product.name} | Buy Online in Pakistan | Loom Electronic Parts</title>
            <meta name="description" content={`Buy ${product.name} online in Pakistan. ${product.description ? product.description.substring(0, 150) : ''} Price: Rs. ${product.price}. Fast delivery across Pakistan.`} />
            <meta name="keywords" content={`${product.name}, ${product.category}, ${product.brand || ''}, buy online Pakistan, loom parts, spare parts`} />
            <meta property="og:title" content={`${product.name} | Loom Electronic Parts`} />
            <meta property="og:description" content={`Buy ${product.name} - Rs. ${product.price}. ${product.description ? product.description.substring(0, 100) : ''}`} />
            <meta property="og:image" content={product.image || product.images?.[0] || 'https://loomelectronicparts.com/logo.png'} />
            <meta property="og:url" content={`https://loomelectronicparts.com/product/${product.slug || product.id}`} />
            <link rel="canonical" href={`https://loomelectronicparts.com/product/${product.slug || product.id}`} />
          </Helmet>
          
          {/* Product Schema (JSON-LD) */}
          <script type="application/ld+json">
            {JSON.stringify({
              "@context": "https://schema.org",
              "@type": "Product",
              "name": product.name,
              "image": product.image || product.images?.[0],
              "description": product.description?.substring(0, 500),
              "brand": {
                "@type": "Brand",
                "name": product.brand || "Generic"
              },
              "offers": {
                "@type": "Offer",
                "url": `https://loomelectronicparts.com/product/${product.slug || product.id}`,
                "price": product.price,
                "priceCurrency": "PKR",
                "availability": product.in_stock ? "https://schema.org/InStock" : "https://schema.org/OutOfStock",
                "seller": {
                  "@type": "Organization",
                  "name": "Loom Electronic Parts"
                }
              },
              ...(feedbacks.length > 0 && {
                "aggregateRating": {
                  "@type": "AggregateRating",
                  "ratingValue": (feedbacks.reduce((sum, f) => sum + f.rating, 0) / feedbacks.length).toFixed(1),
                  "reviewCount": feedbacks.length
                }
              })
            })}
          </script>
        </>
      )}
      <div className="product-detail-container">
        <div className="product-detail-left">
          {product.images && product.images.length > 0 ? (
            <div>
              <img 
                src={product.images[currentImageIndex] || 'https://placehold.co/500x400/e0e0e0/666?text=No+Image'} 
                alt={`Buy ${product.name} - ${product.category} Loom Spare Part Pakistan`} 
                className="product-detail-image"
                loading="lazy"
                style={{cursor: 'pointer'}}
                onClick={() => {
                  setSelectedImage(product.images[currentImageIndex]);
                  setShowImageModal(true);
                }}
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = 'https://placehold.co/500x400/e0e0e0/666?text=No+Image';
                }}
              />
              <div style={{display: 'flex', gap: '8px', marginTop: '12px', overflowX: 'auto'}}>
                {product.images.map((img, index) => (
                  <img 
                    key={index}
                    src={img} 
                    alt={`Thumbnail ${index + 1}`}
                    style={{
                      width: '80px',
                      height: '80px',
                      objectFit: 'cover',
                      borderRadius: '4px',
                      border: currentImageIndex === index ? '3px solid #2196f3' : '1px solid #ddd',
                      cursor: 'pointer',
                      opacity: currentImageIndex === index ? 1 : 0.6
                    }}
                    onClick={() => setCurrentImageIndex(index)}
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = 'https://placehold.co/80x80/e0e0e0/666?text=No+Image';
                    }}
                  />
                ))}
              </div>
            </div>
          ) : (
            <img 
              src={product.image || 'https://placehold.co/500x400/e0e0e0/666?text=No+Image'} 
              alt={`Buy ${product.name} - ${product.category} Loom Spare Part Pakistan`} 
              className="product-detail-image"
              loading="lazy"
              style={{cursor: 'pointer'}}
              onClick={() => {
                setSelectedImage(product.image);
                setShowImageModal(true);
              }}
              onError={(e) => {
                e.target.onerror = null;
                e.target.src = 'https://placehold.co/500x400/e0e0e0/666?text=No+Image';
              }}
            />
          )}
        </div>
        
        <div className="product-detail-right">
          <h1 className="product-detail-title">{product.name}</h1>
          
          <div className="product-detail-price">
            <span>Approx. Price:</span>
            <strong>Rs {product.price}</strong>
            <span>/ Piece</span>
          </div>
          
          {/* Product Stats */}
          {(product.cart_count > 0 || product.sold_count > 0) && (
            <div style={{margin: '12px 0', padding: '10px', background: '#f9f9f9', borderRadius: '6px', borderLeft: '3px solid #ddd'}}>
              <div style={{display: 'flex', gap: '20px', fontSize: '13px', color: '#555'}}>
                {product.cart_count > 0 && (
                  <div style={{display: 'flex', alignItems: 'center', gap: '6px'}}>
                    <span style={{width: '6px', height: '6px', borderRadius: '50%', background: '#1976d2'}}></span>
                    <span>{product.cart_count} times added to cart</span>
                  </div>
                )}
                {product.sold_count > 0 && (
                  <div style={{display: 'flex', alignItems: 'center', gap: '6px'}}>
                    <span style={{width: '6px', height: '6px', borderRadius: '50%', background: '#2e7d32'}}></span>
                    <span>{product.sold_count} sold</span>
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="product-details-section">
            <h3>Product Details:</h3>
            <table className="product-details-table">
              <tbody>
                {product.part_type && (
                  <tr>
                    <td>Part Type</td>
                    <td>{product.part_type}</td>
                  </tr>
                )}
                {product.size && (
                  <tr>
                    <td>Size</td>
                    <td>{product.size}</td>
                  </tr>
                )}
                {product.brand && (
                  <tr>
                    <td>Brand</td>
                    <td>{product.brand}</td>
                  </tr>
                )}
                {product.usage && (
                  <tr>
                    <td>Usage/Application</td>
                    <td>{product.usage}</td>
                  </tr>
                )}
                {product.material && (
                  <tr>
                    <td>Material</td>
                    <td>{product.material}</td>
                  </tr>
                )}
                {product.weight && (
                  <tr>
                    <td>Weight</td>
                    <td>{product.weight}</td>
                  </tr>
                )}
                {product.model_number && (
                  <tr>
                    <td>Model Name/Number</td>
                    <td>{product.model_number}</td>
                  </tr>
                )}
                {product.packaging_size && (
                  <tr>
                    <td>Packaging Size</td>
                    <td>{product.packaging_size}</td>
                  </tr>
                )}
                {product.packaging_type && (
                  <tr>
                    <td>Packaging Type</td>
                    <td>{product.packaging_type}</td>
                  </tr>
                )}
                {product.equipment_type && (
                  <tr>
                    <td>Equipment Type</td>
                    <td>{product.equipment_type}</td>
                  </tr>
                )}
                {getCustomFields().map((field) => (
                  product[field.name] && (
                    <tr key={field.id}>
                      <td>{field.label}</td>
                      <td>{product[field.name]}</td>
                    </tr>
                  )
                ))}
              </tbody>
            </table>
          </div>

          {(product.warranty || product.return_policy) && (
            <div className="product-service-section" style={{marginTop: '20px', padding: '16px', background: '#f0f8ff', borderRadius: '8px', border: '2px solid #2196f3'}}>
              <h3 style={{color: '#2196f3', marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '8px'}}>
                <span>🛡️</span> Service & Warranty
              </h3>
              <div style={{display: 'flex', flexDirection: 'column', gap: '8px'}}>
                {product.warranty && (
                  <div style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
                    <span style={{fontWeight: '600', color: '#555'}}>Warranty:</span>
                    <span style={{color: '#2196f3', fontWeight: '600'}}>{product.warranty}</span>
                  </div>
                )}
                {product.return_policy && (
                  <div style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
                    <span style={{fontWeight: '600', color: '#555'}}>Return Policy:</span>
                    <span style={{color: '#2196f3', fontWeight: '600'}}>{product.return_policy}</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {product.description && (
            <div className="product-description-section">
              <h3>Product Description</h3>
              <div dangerouslySetInnerHTML={{ __html: product.description }} />
            </div>
          )}

          <button 
            className="add-to-cart-btn" 
            onClick={() => {
              onAddToCart(product);
              alert('Product added to cart!');
            }}
          >
            Add to Cart
          </button>
        </div>
      </div>

      <div style={{marginTop: '40px', background: '#fff', padding: '30px', borderRadius: '8px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)'}}>
        <h2 style={{marginBottom: '20px'}}>Customer Reviews ({feedbacks.length})</h2>

        {feedbacks.length === 0 ? (
          <p style={{textAlign: 'center', color: '#999', padding: '40px'}}>No reviews yet. Be the first to review!</p>
        ) : (
          feedbacks.map((feedback) => (
            <div key={feedback.id} style={{borderBottom: '1px solid #eee', padding: '20px 0'}}>
              <div style={{display: 'flex', justifyContent: 'space-between', marginBottom: '10px'}}>
                <div>
                  <strong>{feedback.customer_name}</strong>
                  <div style={{color: '#ffc107', fontSize: '18px'}}>{'★'.repeat(feedback.rating)}{'☆'.repeat(5 - feedback.rating)}</div>
                </div>
                <span style={{color: '#999', fontSize: '14px'}}>{new Date(feedback.created_at).toLocaleDateString()}</span>
              </div>
              {feedback.comment && <p style={{color: '#666', lineHeight: '1.6'}}>{feedback.comment}</p>}
              {feedback.image && (
                <img 
                  src={feedback.image} 
                  alt="Feedback" 
                  style={{marginTop: '10px', maxWidth: '100px', height: '100px', objectFit: 'cover', borderRadius: '8px', cursor: 'pointer'}} 
                  onClick={() => {
                    setSelectedImage(feedback.image);
                    setShowImageModal(true);
                  }}
                />
              )}
            </div>
          ))
        )}
      </div>

      {showImageModal && (
        <div style={{position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', background: 'rgba(0,0,0,0.9)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center'}} onClick={() => setShowImageModal(false)}>
          <img src={selectedImage} alt="Full size" style={{maxWidth: '90%', maxHeight: '90%', objectFit: 'contain'}} onClick={(e) => e.stopPropagation()} />
          <button onClick={() => setShowImageModal(false)} style={{position: 'absolute', top: '20px', right: '20px', background: '#fff', border: 'none', borderRadius: '50%', width: '40px', height: '40px', fontSize: '24px', cursor: 'pointer'}}>×</button>
        </div>
      )}
    </div>
  );
}

export default ProductDetail;
